import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useWebServer } from "../../../../providers";
import usePolling from "../../../../lib/usePollingHook";
import {
  fetchActivityFleet,
  setActivityFlaggedOnly,
  setActivitySort,
  setActivitySearch,
  setActivityUpdatedRange,
  setActivityStateMachine,
  selectActivityDevice,
} from "../../../../actions";
import AccessDeniedPanel from "../../SideBar/DetailsRouter/SuperUserPanel/AccessDenied";
import FleetTable from "./FleetTable";
import FleetFilters from "./FleetFilters";
import DeviceModal from "./DeviceModal";
import {
  ACTIVITY_REFRESH_INTERVAL_MS,
  ACTIVITY_MAX_REFRESH_ROWS,
} from "./constants";

const Index = ({
  superUser,
  orgDevices,
  organizationId,
  flaggedOnly,
  sort,
  search,
  updatedFrom,
  updatedTo,
  stateMachine,
  env,
  fleet = [],
  fleetCursor,
  fleetLoading,
  fleetError,
  selectedDevice,
  fetchActivityFleet,
  setActivityFlaggedOnly,
  setActivitySort,
  setActivitySearch,
  setActivityUpdatedRange,
  setActivityStateMachine,
  selectActivityDevice,
}) => {
  const { sendOrganizationRequest } = useWebServer();

  const refreshingRef = useRef(false);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (superUser) {
      fetchActivityFleet(sendOrganizationRequest, {
        organizationId,
        flaggedOnly,
        sort,
        search,
        updatedFrom,
        updatedTo,
        stateMachine,
        env,
      });
    }
  }, [
    organizationId,
    flaggedOnly,
    sort,
    search,
    updatedFrom,
    updatedTo,
    stateMachine,
    env,
    sendOrganizationRequest,
    fetchActivityFleet,
    superUser,
  ]);

  const refreshRef = useRef({});
  useEffect(() => {
    refreshRef.current = {
      sendOrganizationRequest,
      organizationId,
      flaggedOnly,
      sort,
      search,
      updatedFrom,
      updatedTo,
      stateMachine,
      env,
      loadedRows: fleet.length,
    };
  });

  usePolling(
    () => {
      const { sendOrganizationRequest: send, loadedRows, ...params } =
        refreshRef.current;
      // A refresh replaces the list, so it has to re-request every page the user
      // has loaded - one page would drop the rest off the bottom. Past the cap it
      // stands down rather than send a window the endpoint may refuse to serve.
      if (
        refreshingRef.current ||
        loadingMoreRef.current ||
        loadedRows > ACTIVITY_MAX_REFRESH_ROWS
      ) {
        return;
      }
      refreshingRef.current = true;
      fetchActivityFleet(send, {
        ...params,
        limit: loadedRows || undefined,
        background: true,
      }).finally(() => {
        refreshingRef.current = false;
      });
    },
    ACTIVITY_REFRESH_INTERVAL_MS,
    superUser
  );

  const [searchInput, setSearchInput] = useState(search);

  const debouncedSetActivitySearch = useMemo(
    () => _.debounce((value) => setActivitySearch(value), 400),
    [setActivitySearch]
  );

  useEffect(
    () => () => debouncedSetActivitySearch.cancel(),
    [debouncedSetActivitySearch]
  );

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    debouncedSetActivitySearch(e.target.value);
  };

  const devicesByUuid = useMemo(
    () =>
      Object.values(orgDevices)
        .flat()
        .reduce(
          (curr, tool) => ({
            ...curr,
            [tool.uuid]: {
              displayName: tool.display_name,
              hardwareId: tool.hardware_id,
            },
          }),
          {}
        ),
    [orgDevices]
  );

  const rows = useMemo(
    () =>
      fleet.map((item) => ({
        ...item,
        flagged: item.unreadMaintenanceCount > 0,
        hardwareId: devicesByUuid[item.deviceId]?.hardwareId,
        displayName: devicesByUuid[item.deviceId]?.displayName,
      })),
    [fleet, devicesByUuid]
  );

  // the modal reads the row as the list currently has it, not the copy taken on click:
  // the poll refreshes the row, and a snapshot would let the row and the modal disagree
  // about the device's state. Falls back to the snapshot once a filter drops the row.
  const openDevice = useMemo(
    () =>
      selectedDevice
        ? rows.find((row) => row.deviceId === selectedDevice.deviceId) ??
          selectedDevice
        : null,
    [rows, selectedDevice]
  );

  const handleLoadMore = () => {
    if (loadingMoreRef.current || fleetLoading || !fleetCursor) {
      return;
    }
    loadingMoreRef.current = true;
    fetchActivityFleet(sendOrganizationRequest, {
      organizationId,
      flaggedOnly,
      sort,
      search,
      updatedFrom,
      updatedTo,
      stateMachine,
      env,
      cursor: fleetCursor,
    }).finally(() => {
      loadingMoreRef.current = false;
    });
  };

  return (
    <Paper
      className={
        "flex bg-white rounded-xl border-4 border-transparent transition-opacity duration-1000"
      }
      sx={{
        py: "1rem",
        pt: "3rem",
        px: "1rem",
        width: ["100vw", "100%", "100%", "100%", "100%", "100%"],
        height: "100vh",
      }}
    >
      {superUser ? (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: ["column", "column", "row"],
              justifyContent: "space-between",
              alignItems: ["flex-start", "flex-start", "center"],
              gap: "1rem",
              mb: "1rem",
            }}
          >
            <TextField
              size="small"
              placeholder="Search UUID or hardware ID"
              value={searchInput}
              onChange={handleSearchChange}
              sx={{ minWidth: "12rem", maxWidth: "16rem" }}
            />
            <FleetFilters
              updatedFrom={updatedFrom}
              updatedTo={updatedTo}
              stateMachine={stateMachine}
              onUpdatedRangeChange={setActivityUpdatedRange}
              onStateMachineChange={setActivityStateMachine}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={flaggedOnly}
                  onChange={(e) => setActivityFlaggedOnly(e.target.checked)}
                />
              }
              label="Flagged only"
            />
          </Box>

          <FleetTable
            rows={rows}
            sort={sort}
            loading={fleetLoading}
            error={fleetError}
            onSortChange={setActivitySort}
            onSelectDevice={selectActivityDevice}
          />

          {fleetCursor ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: "1rem" }}>
              <Button
                variant="contained"
                disabled={fleetLoading}
                onClick={handleLoadMore}
              >
                Load more
              </Button>
            </Box>
          ) : null}

          <DeviceModal
            device={openDevice}
            onClose={() => selectActivityDevice(null)}
          />
        </Box>
      ) : (
        <AccessDeniedPanel />
      )}
    </Paper>
  );
};

export const mapStateToProps = (state) => {
  const superUser = state?.user?.superUser || false;
  const orgDevices = state?.user?.orgDevices || {};
  const {
    organizationId,
    flaggedOnly,
    sort,
    search,
    updatedFrom,
    updatedTo,
    stateMachine,
    env,
    fleet,
    fleetCursor,
    fleetLoading,
    fleetError,
    selectedDevice,
  } = state?.activityMonitor || {};
  return {
    superUser,
    orgDevices,
    organizationId,
    flaggedOnly,
    sort,
    search,
    updatedFrom,
    updatedTo,
    stateMachine,
    env,
    fleet,
    fleetCursor,
    fleetLoading,
    fleetError,
    selectedDevice,
  };
};

export default connect(mapStateToProps, {
  fetchActivityFleet,
  setActivityFlaggedOnly,
  setActivitySort,
  setActivitySearch,
  setActivityUpdatedRange,
  setActivityStateMachine,
  selectActivityDevice,
})(Index);
